import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { EventRow } from "@/components/activity/event-row"
import { RecentActivityEmptyState } from "@/components/activity/recent-activity-empty-state"
import { usePreviewMode } from "@/components/profile-preview/preview-mode"
import { Card, CardContent } from "@/components/ui/card"
import { creatorRecentActivityPath } from "@/routes"
import type { ActivityEvent } from "@/types/recent-activity"

/**
 * OKS-393 — `/profile` Recent Activity block (UX Spec §2.1).
 *
 * Additive, self-contained section: a titled card with up to 4
 * `EventRow`s (newest-first) and a right-aligned "See all →" link that
 * deep-links to the standalone `/profile/activity` view. The link only
 * renders when `hasMore` is true (total events > 4 — UX Spec §1 edge).
 *
 * Empty / 1–3-event states are handled inline: zero events → empty
 * state in place of rows (block still renders); 1–3 events → only those
 * rows, no padding, no "See all".
 *
 * All data is server-supplied via Inertia props scoped to
 * `Current.membership` — no client-side query, no cross-brand surface.
 */
interface RecentActivityCardProps {
  events: ActivityEvent[]
  hasMore: boolean
}

export function RecentActivityCard({
  events,
  hasMore,
}: RecentActivityCardProps) {
  const { t } = useTranslation()
  const preview = usePreviewMode()

  return (
    <Card data-testid="recent-activity-card" className="border-0 shadow-none">
      <CardContent className="flex flex-col gap-3 px-0 py-4">
        <h2 className="text-base font-semibold">
          {t("recent_activity.title")}
        </h2>

        {events.length === 0 ? (
          <RecentActivityEmptyState />
        ) : (
          <>
            <ul className="border-border overflow-hidden rounded-lg border">
              {events.map((event) => (
                <EventRow key={event.id} event={event} variant="panel" />
              ))}
            </ul>

            {hasMore && (
              <div className="flex justify-end">
                {/* OKS-400 — inert in read-only preview (no navigation). */}
                {preview ? (
                  <span
                    aria-disabled="true"
                    className="text-muted-foreground/60 cursor-not-allowed text-sm"
                    data-testid="recent-activity-see-all"
                  >
                    {t("recent_activity.see_all")}
                  </span>
                ) : (
                  <Link
                    href={creatorRecentActivityPath()}
                    className="text-muted-foreground hover:text-foreground text-sm hover:underline"
                    data-testid="recent-activity-see-all"
                  >
                    {t("recent_activity.see_all")}
                  </Link>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
